import type { Connection, Edge } from '../types';

const isEdge = (value: Edge | Connection): value is Edge => 'id' in value;

const sameHandle = (a: string | null | undefined, b: string | null | undefined): boolean => a === b || (!a && !b);

/** v11 duplicate check: same ends and same handles (a missing handle equals another missing handle). */
const connectionExists = (edge: Edge, edges: readonly Edge[]): boolean =>
  edges.some(
    (existing) =>
      existing.source === edge.source &&
      existing.target === edge.target &&
      sameHandle(existing.sourceHandle, edge.sourceHandle) &&
      sameHandle(existing.targetHandle, edge.targetHandle),
  );

/** Id for edges created from a bare connection (the app always passes its own `edge-<uuid>`). */
const connectionEdgeId = ({ source, sourceHandle, target, targetHandle }: Connection): string =>
  `edge-${source ?? ''}${sourceHandle ?? ''}-${target ?? ''}${targetHandle ?? ''}`;

/**
 * Returns a new array with the edge added, or the same `edges` array when an end is missing or the same connection
 * exists (v11 `addEdge`). The app always passes an edge with its own `edge-<uuid>` id.
 */
export const addEdge = <TEdge extends Edge>(edgeOrConnection: TEdge | Connection, edges: TEdge[]): TEdge[] => {
  if (!edgeOrConnection.source || !edgeOrConnection.target) return edges;
  const edge = (isEdge(edgeOrConnection)
    ? { ...edgeOrConnection }
    : { ...edgeOrConnection, id: connectionEdgeId(edgeOrConnection) }) as TEdge;
  if (connectionExists(edge, edges)) return edges;
  return [...edges, edge];
};
